import axios from 'axios';
import Cookies from 'js-cookie';
import jwtDecode from 'jwt-decode';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://sgpnc-v2.vercel.app/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Try to get token from localStorage first, then cookies
    const token = localStorage.getItem('token') || Cookies.get('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect to login if it's a 401 error AND not a login attempt AND not already on login page
    if (
      error.response?.status === 401 &&
      !error.config.url?.includes('/users/login') &&
      window.location.pathname !== '/login'
    ) {
      // Clear all auth data on 401
      Cookies.remove('token');
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth service
export const authService = {
  login: async (credentials) => {
    const response = await api.post('/users/login', credentials);
    if (response.data.token) {
      // Store token in both localStorage and cookies
      localStorage.setItem('token', response.data.token);
      Cookies.set('token', response.data.token, { expires: 7 });

      // Store complete user data from login response in localStorage
      const userData = {
        _id: response.data._id,
        name: response.data.name,
        email: response.data.email,
        role: response.data.role,
        plant: response.data.plant,
        plantName: response.data.plantName,
        token: response.data.token,
        // Also include JWT decoded data for compatibility
        ...jwtDecode(response.data.token)
      };
      localStorage.setItem('user', JSON.stringify(userData));
    }
    return response.data;
  },

  logout: () => {
    // Clear all auth data
    Cookies.remove('token');
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/login';
  },

  getCurrentUser: () => {
    // First try to get from localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch (error) {
        localStorage.removeItem('user');
      }
    }

    // Fallback to JWT decode from cookie
    const token = Cookies.get('token');
    if (token) {
      try {
        const userData = jwtDecode(token);
        // Store in localStorage for future use
        localStorage.setItem('user', JSON.stringify(userData));
        return userData;
      } catch (error) {
        Cookies.remove('token');
        localStorage.removeItem('user');
        return null;
      }
    }
    return null;
  },

  forgotPassword: async (email) => {
    const response = await api.post('/users/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (data) => {
    const response = await api.post('/users/reset-password', data);
    return response.data;
  },

  resendOTP: async (email) => {
    const response = await api.post('/users/resend-otp', { email });
    return response.data;
  },

  verifyToken: async () => {
    const response = await api.get('/users/verify');
    return response.data;
  },
};

// User service
export const userService = {
  getUsers: async (params = {}) => {
    const response = await api.get('/users', { params });
    return response.data;
  },

  getUserById: async (id) => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  createUser: async (userData) => {
    const response = await api.post('/users/register', userData);
    return response.data;
  },

  updateUser: async (userId, userData) => {
    const response = await api.put(`/users/${userId}`, userData);
    return response.data;
  },

  deleteUser: async (userId) => {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  },
};

// Plant service
export const plantService = {
  getPlants: async (params = {}) => {
    const response = await api.get('/plants', { params });
    return response.data;
  },

  getPlantById: async (id) => {
    const response = await api.get(`/plants/${id}`);
    return response.data;
  },

  createPlant: async (plantData) => {
    const response = await api.post('/plants', plantData);
    return response.data;
  },

  updatePlant: async (plantId, plantData) => {
    const response = await api.put(`/plants/${plantId}`, plantData);
    return response.data;
  },

  deletePlant: async (plantId) => {
    const response = await api.delete(`/plants/${plantId}`);
    return response.data;
  },
};

// Compliance service
export const complianceService = {
  getCompliances: async (params = {}) => {
    const response = await api.get('/compliances', { params });
    return response.data;
  },

  getComplianceById: async (id) => {
    const response = await api.get(`/compliances/${id}`);
    return response.data;
  },

  createCompliance: async (complianceData) => {
    // Backward compatibility: if `plants` provided, ensure legacy `plant` is set to first
    const payload = { ...complianceData };
    if (Array.isArray(payload.plants) && payload.plants.length > 0) {
      payload.plant = payload.plants[0];
    }
    const response = await api.post('/compliances', payload);
    return response.data;
  },

  importCompliances: async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post("/compliances/bulk-compliance", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 120000,
    });
    return response.data;
  },

  updateCompliance: async (id, complianceData) => {
    // Keep legacy `plant` in sync with first selected plant
    const payload = { ...complianceData };
    if (Array.isArray(payload.plants) && payload.plants.length > 0) {
      payload.plant = payload.plants[0];
    }
    const response = await api.put(`/compliances/${id}`, payload);
    return response.data;
  },

  bulkDeleteCompliances: async (ids) => {
    const response = await api.post('/compliances/bulk-delete', { ids });
    return response.data;
  },

  deleteCompliance: async (id) => {
    const response = await api.delete(`/compliances/${id}`);
    return response.data;
  },

  submitForVerification: async (id, data) => {
    const response = await api.put(`/compliances/${id}/submit`, data);
    return response.data;
  },

  verifyCompliance: async (id, data) => {
    const response = await api.put(`/compliances/${id}/verify`, data);
    return response.data;
  },

  uploadAttachment: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/compliances/upload-attachment', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return response.data;
  },

  getComplianceStats: async (params = {}) => {
    const response = await api.get('/compliances/stats', { params });
    return response.data;
  },

  sendReminders: async () => {
    const response = await api.post('/compliances/send-reminders');
    return response.data;
  },

  // Excel import/export
  importExcel: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/compliances/import-excel', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000,
    });
    return response.data;
  },

  downloadTemplate: async () => {
    const response = await api.get('/compliances/download-template', {
      responseType: 'blob',
    });
    return response.data;
  },

  exportCompliances: async (params = {}) => {
    const response = await api.get('/compliances/export', {
      params,
    });
    return response.data;
  },
};

// Utility functions
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export default api;